/**
 * ViewProductPage Component
 * Read-only page for viewing product details
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerDashboardLayout } from '../components/SellerDashboardLayout';
import { LoadingSpinner } from '@/components/ui';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, ArrowLeft, Calendar, Package, DollarSign } from 'lucide-react';
import { microservicesClient } from '@/services/microservices/index';
import { ProductStatusBadge } from '../components/ProductStatusBadge';
import { getProductsQuantities } from '../services/inventoryBridgeService';
import { salesService } from '@/modules/sales/services/salesService';
import './ViewProductPage.css';

/**
 * Props interface for ViewProductPage component
 */
export interface ViewProductPageProps {
  /** When true, renders without SellerDashboardLayout wrapper (for embedding in other layouts) */
  disableLayout?: boolean;
}

/**
 * Product data retrieved from API
 */
interface ProductData {
  id: string;
  sellerId?: string;
  name?: string;
  productName?: string;
  category: string;
  description: string;
  price: number;
  unit: string;
  quantity?: number;
  quantityAvailable?: number;
  minimumOrderQuantity?: number;
  state?: string;
  district?: string;
  pincode?: string;
  address?: string;
  status: string;
  images: Array<{
    imageId: string;
    url: string;
  }> | string[];
  inquiryCount?: number;
  totalInquiries?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * ViewProductPage Component
 * Displays product information in a read-only format
 *
 * @component
 * @example
 * <ViewProductPage disableLayout={true} />
 */
export const ViewProductPage: React.FC<ViewProductPageProps> = ({
  disableLayout = false,
}) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [availableQuantity, setAvailableQuantity] = useState<number | null>(null);
  const [quantityLoading, setQuantityLoading] = useState(false);

  // Set page title and meta description on mount
  useEffect(() => {
    document.title = 'View Product | SAUBHAGYA Seller Dashboard';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'View your product details on the SAUBHAGYA marketplace'
      );
    }
  }, []);

  // Load product data from API
  useEffect(() => {
    if (!productId) {
      setError('Product ID is missing');
      setIsLoading(false);
      return;
    }

    const loadProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[ViewProductPage] Loading product:', productId);

        const response = await microservicesClient.callService(
          'commerce-service',
          `/api/v1/marketplace/products/${productId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Product not found');
          }
          if (response.status === 403) {
            throw new Error('You do not have permission to view this product');
          }
          throw new Error('Failed to load product');
        }

        const data = await response.json();
        const productData = data.data || data;

        if (!productData || !productData.id) {
          throw new Error('Invalid response structure');
        }

        console.log('[ViewProductPage] Successfully loaded product:', {
          id: productData.id,
          name: productData.name || productData.productName,
        });

        setProduct(productData);
      } catch (err) {
        console.error('[ViewProductPage] Error loading product:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load product';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productId]);

  // Fetch real available quantity from inventory service
  useEffect(() => {
    if (!product || !product.id) {
      setAvailableQuantity(null);
      setQuantityLoading(false);
      return;
    }

    const fetchQuantity = async () => {
      setQuantityLoading(true);
      try {
        // Convert product ID to string for consistency
        const productIdStr = String(product.id);
        console.log('[ViewProductPage] Fetching real available quantity for product:', productIdStr);

        const quantityMap = await getProductsQuantities([productIdStr]);

        // Log what's in the map for debugging
        console.log('[ViewProductPage] Quantity map size:', quantityMap.size);
        console.log('[ViewProductPage] Quantity map keys:', Array.from(quantityMap.keys()));

        // Try to get with the product ID (should be in the map as string)
        const qty = quantityMap.get(productIdStr);

        if (qty && qty.availableQuantity > 0) {
          console.log('[ViewProductPage] ✅ Successfully fetched available quantity from inventory:', qty.availableQuantity, qty.unit);
          setAvailableQuantity(qty.availableQuantity);
        } else {
          console.log('[ViewProductPage] ⚠️ No quantity data from inventory API (0 or failed), trying fallback...');

          // Fallback 1: Try to fetch from salesService using product name
          const productName = product.name || product.productName;
          let fallbackAvailableQty: number | null = null;

          if (productName) {
            try {
              const fallbackQty = await salesService.getProductQuantities(productName);
              fallbackAvailableQty = fallbackQty.available !== undefined ? fallbackQty.available : fallbackQty.availableQuantity || 0;
              console.log('[ViewProductPage] Fallback 1 (salesService) quantity:', fallbackAvailableQty);
            } catch (fallbackErr) {
              console.error('[ViewProductPage] Fallback 1 (salesService) failed:', fallbackErr);
            }
          }

          // Fallback 2: If fallback 1 returned 0 or failed, use product API's quantityAvailable field
          if (!fallbackAvailableQty || fallbackAvailableQty === 0) {
            const apiQuantity = product.quantity || product.quantityAvailable || 0;
            if (apiQuantity > 0) {
              console.log('[ViewProductPage] ✅ Using Fallback 2 (product API):', apiQuantity);
              setAvailableQuantity(apiQuantity);
            } else {
              console.log('[ViewProductPage] ⚠️ All fallbacks returned 0');
              setAvailableQuantity(fallbackAvailableQty || 0);
            }
          } else {
            console.log('[ViewProductPage] ✅ Using Fallback 1 (salesService):', fallbackAvailableQty);
            setAvailableQuantity(fallbackAvailableQty);
          }
        }
      } catch (err) {
        console.error('[ViewProductPage] ❌ Error fetching available quantity:', err);
        setAvailableQuantity(null);
      } finally {
        setQuantityLoading(false);
      }
    };

    fetchQuantity();
  }, [product?.id, product?.name, product?.productName]);

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    // Detect if we're in sales or marketplace context
    if (window.location.pathname.includes('/sales')) {
      navigate('/sales/my-products');
    } else {
      navigate('/marketplace/seller/products');
    }
  };

  /**
   * Transform images to array of URLs
   * Image URLs like /uploads/... are served via Vite proxy (dev) or gateway (prod)
   */
  const getImageUrls = (images: ProductData['images']): string[] => {
    if (!Array.isArray(images)) return [];

    return images.map((img) => {
      if (typeof img === 'string') return img;
      if (img && typeof img === 'object' && 'url' in img) return img.url;
      return '';
    }).filter(Boolean);
  };

  // Helper to wrap content with or without layout
  const wrapContent = (content: React.ReactNode, subtitle?: string) => {
    if (disableLayout) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">View Product</h2>
              {subtitle && <p className="text-gray-600 text-sm mt-1">{subtitle}</p>}
            </div>
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Products
            </Button>
          </div>
          {content}
        </div>
      );
    }

    return (
      <SellerDashboardLayout
        activeNavItemId="my-products"
        breadcrumbs={[
          { label: 'Dashboard', path: '/marketplace/seller/dashboard' },
          { label: 'My Products', path: '/marketplace/seller/products' },
          { label: 'View Product' },
        ]}
        hideStats={true}
        pageTitle="View Product"
        pageSubtitle={subtitle}
      >
        {content}
      </SellerDashboardLayout>
    );
  };

  // Render loading state
  if (isLoading) {
    return wrapContent(
      <div className="view-product-page-loading" data-testid="view-product-loading">
        <div className="loading-container flex flex-col items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return wrapContent(
      <div className="view-product-page-error" data-testid="view-product-error">
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Product</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={handleBack} variant="outline">
              Back to My Products
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Render not found state
  if (!product) {
    return wrapContent(
      <div className="view-product-page-error" data-testid="view-product-not-found">
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-gray-600 mb-6">
              The product you are trying to view could not be found.
            </p>
            <Button onClick={handleBack} variant="outline">
              Back to My Products
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const imageUrls = getImageUrls(product.images);
  const productName = product.name || product.productName || 'Untitled Product';

  // Render product view
  return wrapContent(
    <div className="view-product-page" data-testid="view-product-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Images */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Images</h3>

            {imageUrls.length > 0 ? (
              <div className="space-y-4">
                {/* Main Image */}
                <div className="aspect-square rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                  <img
                    src={imageUrls[currentImageIndex]}
                    alt={`${productName} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const fallback = target.parentElement?.querySelector('.image-fallback');
                      if (fallback) (fallback as HTMLElement).style.display = 'flex';
                    }}
                  />
                  <div className="image-fallback hidden items-center justify-center w-full h-full text-gray-400" style={{ display: 'none' }}>
                    <div className="text-center">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Image unavailable</p>
                    </div>
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                {imageUrls.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {imageUrls.map((url, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-square rounded border-2 overflow-hidden transition-all ${
                          currentImageIndex === index
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No images available</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Right Column - Product Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-2xl font-bold text-gray-900">{productName}</h3>
              <ProductStatusBadge status={product.status as any} />
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <div className="mt-1">
                  <Badge variant="secondary" className="text-sm">
                    {product.category}
                  </Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="mt-1 text-gray-900 whitespace-pre-wrap">{product.description}</p>
              </div>
            </div>
          </Card>

          {/* Pricing Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing Details
            </h3>

            <div className={`grid gap-4 ${product.minimumOrderQuantity ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
              <div>
                <label className="text-sm font-medium text-gray-500">Price</label>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  ₹{(product.price ?? 0).toLocaleString('en-IN')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Unit</label>
                <p className="mt-1 text-lg text-gray-900">{product.unit}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Quantity Available</label>
                <p className="mt-1 text-lg text-gray-900">
                  {quantityLoading ? (
                    <span className="text-gray-400">Loading...</span>
                  ) : availableQuantity !== null ? (
                    <>{availableQuantity} {product.unit || 'kg'}</>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>

              {product.minimumOrderQuantity && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="text-sm font-medium text-amber-900">Minimum Order Quantity</label>
                  <p className="mt-1 text-lg font-semibold text-amber-900">
                    {product.minimumOrderQuantity} {product.unit}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Location Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Location
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.state && (
                <div>
                  <label className="text-sm font-medium text-gray-500">State</label>
                  <p className="mt-1 text-gray-900">{product.state}</p>
                </div>
              )}

              {product.district && (
                <div>
                  <label className="text-sm font-medium text-gray-500">District</label>
                  <p className="mt-1 text-gray-900">{product.district}</p>
                </div>
              )}

              {product.pincode && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Pincode</label>
                  <p className="mt-1 text-gray-900">{product.pincode}</p>
                </div>
              )}

              {product.address && (
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <p className="mt-1 text-gray-900">{product.address}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Additional Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Additional Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(product.inquiryCount !== undefined || product.totalInquiries !== undefined) && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Inquiries</label>
                  <p className="mt-1 text-lg font-semibold text-blue-600">
                    {product.inquiryCount || product.totalInquiries || 0}
                  </p>
                </div>
              )}

              {product.createdAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Created Date</label>
                  <p className="mt-1 text-gray-900">
                    {new Date(product.createdAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              )}

              {product.updatedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Updated</label>
                  <p className="mt-1 text-gray-900">
                    {new Date(product.updatedAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>,
    productName
  );
};

export default ViewProductPage;
